import axios from 'axios';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Vendor authentication methods
export const vendorAuthAPI = {
  // Send OTP to mobile number for vendor
  sendOTP: async (mobile) => {
    const response = await api.post('/api/vendor-auth/send-otp', { mobile });
    return response.data;
  },

  // Verify OTP and login/register vendor
  verifyOTP: async (mobile, otp, name = null, email = null) => {
    const payload = { mobile, otp };
    if (name) payload.name = name;
    if (email) payload.email = email;
    
    const response = await api.post('/api/vendor-auth/verify-otp', payload);
    return response.data;
  },
};

// Vendor stays and rooms management
const vendorApi = {
  // ==================== STAY MANAGEMENT ====================
  
  // Get all stays for vendor
  getStays: async () => {
    try {
      const response = await api.get('/api/vendor/stays');
      return response.data;
    } catch (error) {
      console.error('Error fetching vendor stays:', error);
      throw error;
    }
  },

  // Get single stay
  getStay: async (stayId) => {
    try {
      const response = await api.get(`/api/vendor/stays/${stayId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching vendor stay:', error);
      throw error;
    }
  },

  // Create new stay
  createStay: async (stayData) => {
    try {
      const response = await api.post('/api/vendor/stays', stayData);
      return response.data;
    } catch (error) {
      console.error('Error creating vendor stay:', error);
      throw error;
    }
  },

  // Update stay
  updateStay: async (stayId, stayData) => {
    try {
      const response = await api.put(`/api/vendor/stays/${stayId}`, stayData);
      return response.data;
    } catch (error) {
      console.error('Error updating vendor stay:', error);
      throw error;
    }
  },

  // Delete stay
  deleteStay: async (stayId) => {
    try {
      const response = await api.delete(`/api/vendor/stays/${stayId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting vendor stay:', error);
      throw error;
    }
  },

  // ==================== ROOM MANAGEMENT ====================

  // Get all rooms for vendor's stays
  getRooms: async (stayId = null) => {
    try {
      const params = stayId ? { stayId } : {};
      const response = await api.get('/api/vendor/rooms', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching vendor rooms:', error);
      throw error;
    }
  },

  // Create new room
  createRoom: async (roomData) => {
    try {
      const response = await api.post('/api/vendor/rooms', roomData);
      return response.data;
    } catch (error) {
      console.error('Error creating vendor room:', error);
      throw error;
    }
  },

  // Update room
  updateRoom: async (stayId, roomId, roomData) => {
    try {
      const response = await api.put(`/api/vendor/rooms/${stayId}/${roomId}`, roomData);
      return response.data;
    } catch (error) {
      console.error('Error updating vendor room:', error);
      throw error;
    }
  },

  // Delete room
  deleteRoom: async (stayId, roomId) => {
    try {
      const response = await api.delete(`/api/vendor/rooms/${stayId}/${roomId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting vendor room:', error);
      throw error;
    }
  },

  // ==================== INVENTORY ====================

  getInventoryCalendar: async (stayId, days = 30) => {
    const response = await api.get(`/api/vendor/inventory/stays/${stayId}/calendar`, {
      params: { days }
    });
    return response.data;
  },

  ensureInventoryWindow: async (stayId, days = 30) => {
    const response = await api.post(
      `/api/vendor/inventory/stays/${stayId}/ensure-window`,
      { days }
    );
    return response.data;
  },

  patchInventoryDay: async (stayId, roomId, date, payload) => {
    const response = await api.patch(
      `/api/vendor/inventory/stays/${stayId}/rooms/${roomId}/dates/${date}`,
      payload
    );
    return response.data;
  },

  getInventoryAudit: async (stayId, limit = 40, cursor = null) => {
    const params = { limit };
    if (cursor) params.cursor = cursor;
    const response = await api.get(`/api/vendor/inventory/stays/${stayId}/audit`, {
      params
    });
    return response.data;
  },

  // ==================== BOOKINGS & GROWTH ====================

  getBookings: async () => {
    const response = await api.get('/api/vendor/bookings');
    return response.data;
  },

  approveBooking: async (bookingId, note = '') => {
    const response = await api.post(`/api/vendor/bookings/${bookingId}/approve`, { note });
    return response.data;
  },

  declineBooking: async (bookingId, note = '') => {
    const response = await api.post(`/api/vendor/bookings/${bookingId}/decline`, { note });
    return response.data;
  },

  cancelApprovedBooking: async (bookingId, note = '') => {
    const response = await api.post(`/api/vendor/bookings/${bookingId}/cancel`, { note });
    return response.data;
  },

  getGrowthStats: async (period = 'week') => {
    const response = await api.get('/api/vendor/growth-stats', {
      params: { period }
    });
    return response.data;
  },
};

export default vendorApi;
